import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    CircularProgress,
    Paper,
    Stack,
    Typography,
    useTheme
} from "@mui/material";
import { SwapHoriz } from "@mui/icons-material";
import { fetchWithError } from "../utils/fetchWithError.ts";
import { useSnackbar } from "notistack";
import tintoaLogoWhite from "../img/logo_tintoa_white.png";
import tintoaLogoBlack from "../img/logo_tintoa_black.png";
import type {
    IInvitationClaimInfo,
    IInvitationClaimResult
} from "@ids/shared";

/**
 * Shown when an invitation link is opened while a different user is logged
 * in on this browser. The user explicitly confirms the account switch – the
 * foreign session is never replaced silently.
 */
export const InviteMismatchScreen = () => {
    const token = new URLSearchParams(window.location.search).get("token") ?? "";
    const [info, setInfo] = useState<IInvitationClaimInfo | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const theme = useTheme();
    const isDarkTheme = theme.palette.mode === "dark";
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        const loadInfo = async () => {
            try {
                const result: IInvitationClaimInfo = await fetchWithError(
                    `/auth/invite/info?token=${encodeURIComponent(token)}`,
                    { credentials: "include" }
                );
                if (!result.valid) {
                    navigate("/app/invite-expired", { replace: true });
                    return;
                }
                if (!result.sessionMismatch) {
                    // Session belongs to the invited user (or none): claim directly.
                    window.location.href = `/auth/invite?token=${encodeURIComponent(token)}`;
                    return;
                }
                setInfo(result);
            } catch {
                navigate("/app/invite-expired", { replace: true });
            }
        };
        loadInfo();
    }, [token, navigate]);

    const handleContinue = async () => {
        setIsLoading(true);
        try {
            const result: IInvitationClaimResult = await fetchWithError(
                "/auth/invite/continue",
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ token }),
                    credentials: "include"
                }
            );
            window.location.href = result.redirectUrl;
        } catch (error) {
            enqueueSnackbar(
                error instanceof Error
                    ? error.message
                    : "Die Einladung konnte nicht geöffnet werden.",
                { variant: "error" }
            );
            setIsLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: "background.default",
                p: 2
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    p: { xs: 3, sm: 5 },
                    maxWidth: 520,
                    width: "100%",
                    textAlign: "center",
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 2
                }}
            >
                {!info ? (
                    <Box sx={{ py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <img
                            src={isDarkTheme ? tintoaLogoWhite : tintoaLogoBlack}
                            alt="tintoa-logo"
                            style={{ height: 32, marginBottom: 24 }}
                        />
                        <SwapHoriz
                            sx={{
                                fontSize: 64,
                                color: "text.secondary",
                                mb: 2
                            }}
                        />
                        <Typography variant="h5" gutterBottom>
                            Diese Einladung gilt für ein anderes Konto
                        </Typography>
                        <Typography color="text.secondary" sx={{ mb: 1 }}>
                            Die Einladung
                            {info.clientAppName
                                ? ` zu ${info.clientAppName}`
                                : ""}{" "}
                            gilt für <strong>{info.email}</strong>.
                        </Typography>
                        {info.sessionEmail && (
                            <Typography color="text.secondary" sx={{ mb: 3 }}>
                                In diesem Browser ist derzeit{" "}
                                <strong>{info.sessionEmail}</strong> angemeldet.
                            </Typography>
                        )}
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.5}
                            justifyContent="center"
                        >
                            <Button
                                variant="contained"
                                onClick={handleContinue}
                                disabled={isLoading}
                            >
                                {isLoading
                                    ? "Wird gewechselt..."
                                    : `Als ${info.email} fortfahren`}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => navigate("/app/profile")}
                                disabled={isLoading}
                            >
                                Abbrechen
                            </Button>
                        </Stack>
                    </>
                )}
            </Paper>
        </Box>
    );
};
